import { Bookmark, BookmarkPlus, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useAccess } from '@/lib/access';
import { useSearchParams } from '@/lib/scopedParams';

interface SavedView {
  name: string;
  query: string;
}

function read(key: string): SavedView[] {
  try {
    const v = JSON.parse(window.localStorage.getItem(key) ?? '[]') as unknown;
    return Array.isArray(v) ? v.filter((x): x is SavedView => typeof x?.name === 'string' && typeof x?.query === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * "Bộ lọc đã lưu" of a list: the current filters (`keys` of the URL) saved under a name, one
 * click to apply; `reset` are the paging keys dropped on apply. The server has no store for staff
 * preferences, so views live in this browser, per signed-in staff member (`cc.views.<list>.<user>`).
 */
export function SavedViews({ list, keys, reset = [] }: { list: string; keys: readonly string[]; reset?: readonly string[] }) {
  const { userId } = useAccess();
  // Remounted per user so one person's views never show under another's name.
  return userId ? <Views key={userId} storageKey={`cc.views.${list}.${userId}`} keys={keys} reset={reset} /> : null;
}

function Views({ storageKey, keys, reset }: { storageKey: string; keys: readonly string[]; reset: readonly string[] }) {
  const { t } = useTranslation('ux');
  const [params, setParams] = useSearchParams();
  const [views, setViews] = useState<SavedView[]>(() => read(storageKey));
  const current = new URLSearchParams();
  for (const k of keys) {
    const v = params.get(k);
    if (v) current.set(k, v);
  }
  const query = current.toString();
  const store = (next: SavedView[]) => {
    setViews(next);
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      // Storage unavailable (private mode): the views last for this page only.
    }
  };
  const save = () => {
    const name = window.prompt(t('views.namePrompt'))?.trim();
    if (name) store([...views.filter((v) => v.name !== name), { name, query }]);
  };
  const apply = (v: SavedView) => {
    const next = new URLSearchParams(params);
    for (const k of [...keys, ...reset]) next.delete(k);
    for (const [k, val] of new URLSearchParams(v.query)) next.set(k, val);
    setParams(next);
  };
  if (!views.length && !query) return null;
  return (
    <div className="mb-3 flex max-w-full flex-wrap items-center gap-2" role="group" aria-label={t('views.saved')} data-saved-views>
      <span className="text-xs font-medium text-muted-foreground">{t('views.saved')}</span>
      {views.map((v) => (
        <span key={v.name} className={`inline-flex max-w-full items-center rounded-full border text-sm ${v.query === query ? 'border-primary bg-primary/10 text-primary' : ''}`}>
          <button type="button" className="inline-flex min-w-0 items-center gap-1 py-1 pr-1 pl-3" onClick={() => apply(v)}>
            <Bookmark className="size-3.5 shrink-0" aria-hidden="true" /> <span className="truncate">{v.name}</span>
          </button>
          <button type="button" className="rounded-full p-1 pr-2 text-muted-foreground hover:text-foreground" aria-label={t('views.remove', { name: v.name })} onClick={() => store(views.filter((x) => x.name !== v.name))}>
            <X className="size-3.5" aria-hidden="true" />
          </button>
        </span>
      ))}
      {query && !views.some((v) => v.query === query) ? (
        <Button type="button" variant="ghost" size="sm" onClick={save}>
          <BookmarkPlus /> {t('views.save')}
        </Button>
      ) : null}
      <span className="text-xs text-muted-foreground">{t('views.local')}</span>
    </div>
  );
}
